// Polls GET /laser/{task_id}/dock_check while the Add Sheet dialog is open (laserDock.js has the rules).
//
// - On open, then every `pollMs` (2 s) while the dock decides this task (`required` true), every `slowMs`
//   (10 s) while it does not, so plugging / unplugging a pallet fixes the dialog on the spot.
// - Paused while the tab is hidden (no timer runs); a read as soon as it is shown again.
// - Never two requests at once: a re-check asked for during a read runs right after it.
// - Stops on close (open false / unmount); an answer that lands after that is dropped. A new task_id
//   starts from nothing (the last task's answer is never shown for this one).
// - 400 / 404 / 405 = no endpoint: polling stops, `check` null (today's dialog). Anything else that is
//   not an answer (401, 403, 429, 5xx, no answer) keeps the last answer and backs off (2 -> 4 -> 8 s,
//   at most 10 s).
// - `stale`: no fresh answer for `staleMs` (10 s) since the last one (the dialog then shows
//   "Checking the dock…" instead of a green card and holds Confirm back).
// - No input for `idleMs` (10 min, `activity()` says there was some): polling stops until the next input.
// - `seed(check)`: an answer that came another way (GET tracking_required's `dock` block) used only
//   while no read has answered yet.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DOCK_POLL_MS, DOCK_SLOW_POLL_MS, DOCK_STALE_MS, DOCK_IDLE_MS, dockRetryDelay, loadDockCheck,
} from './laserDock';

const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
const sameAnswer = (a, b) => {
  try { return a !== null && b !== null && JSON.stringify(a) === JSON.stringify(b); } catch (_) { return false; }
};

export default function useLaserDockCheck(taskId, open, {
  pollMs = DOCK_POLL_MS, slowMs = DOCK_SLOW_POLL_MS, staleMs = DOCK_STALE_MS, idleMs = DOCK_IDLE_MS,
  load = loadDockCheck,
} = {}) {
  const [check, setCheck] = useState(null);
  const [stale, setStale] = useState(false);
  const checkRef = useRef(null);
  const recheckRef = useRef(() => {});
  const activityRef = useRef(() => {});
  const seedRef = useRef(() => {});

  useEffect(() => {
    if (!open || !taskId) return undefined;
    let stopped = false;
    let timer = null;
    let staleTimer = null;
    let inFlight = false;
    let again = false;
    let failures = 0;
    let missing = false;
    let answered = false;
    let idle = false;
    let lastActivity = Date.now();

    // A new task (or reopening) starts from nothing.
    if (checkRef.current !== null) { checkRef.current = null; setCheck(null); }
    setStale(false);

    const take = (c) => {
      if (!sameAnswer(checkRef.current, c)) { checkRef.current = c; setCheck(c); }
      setStale(false);
      clearTimeout(staleTimer);
      staleTimer = setTimeout(() => { if (!stopped) setStale(true); }, staleMs);
    };
    const nextDelay = () => {
      if (failures) return dockRetryDelay(failures, pollMs);
      return checkRef.current && checkRef.current.required === true ? pollMs : Math.max(slowMs, pollMs);
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = null;
      if (!stopped && !missing && !idle && !hidden()) timer = setTimeout(tick, nextDelay());
    };
    async function tick() {
      if (stopped || missing) return;
      if (inFlight) { again = true; return; }
      if (hidden()) return;                  // the visibilitychange listener starts it again
      if (Date.now() - lastActivity > idleMs) { idle = true; return; }   // activity() starts it again
      inFlight = true;
      let r;
      try { r = await load(taskId); } catch (_) { r = { failed: true }; }
      inFlight = false;
      if (stopped) return;                   // closed while it was on its way
      if (r && r.missing) {
        missing = true;                      // no endpoint: never ask again for this dialog
        clearTimeout(staleTimer);
        if (checkRef.current !== null) { checkRef.current = null; setCheck(null); }
        return;
      }
      if (r && r.check) {
        answered = true;
        failures = 0;
        take(r.check);
      } else {
        failures += 1;
      }
      if (again) { again = false; tick(); return; }
      schedule();
    }

    const onVisibility = () => {
      clearTimeout(timer);
      timer = null;
      if (stopped || hidden()) return;
      tick();
    };
    recheckRef.current = () => {
      if (stopped) return;
      lastActivity = Date.now();
      idle = false;
      clearTimeout(timer);
      timer = null;
      tick();
    };
    activityRef.current = () => {
      lastActivity = Date.now();
      if (idle && !stopped) { idle = false; tick(); }
    };
    seedRef.current = (c) => {
      if (stopped || missing || answered || !c || typeof c !== 'object') return;
      take(c);
    };
    document.addEventListener('visibilitychange', onVisibility);
    tick();
    return () => {
      stopped = true;
      clearTimeout(timer);
      clearTimeout(staleTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      recheckRef.current = () => {};
      activityRef.current = () => {};
      seedRef.current = () => {};
    };
  }, [taskId, open, pollMs, slowMs, staleMs, idleMs, load]);

  const recheck = useCallback(() => recheckRef.current(), []);
  const activity = useCallback(() => activityRef.current(), []);
  const seed = useCallback((c) => seedRef.current(c), []);
  return { check: open ? check : null, stale: open ? stale : false, recheck, activity, seed };
}
